import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Tabs,
  Tab,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import EditIcon from '@mui/icons-material/Edit';
import ImageIcon from '@mui/icons-material/Image';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { useTranslation } from 'react-i18next';
import AddNoteDialog from './AddNoteDialog';
import EditNoteDialog from './EditNoteDialog';
import CardHeader from '../shared/CardHeader';

function NotesSection({ maintenance, setMaintenance }) {
  const { t } = useTranslation();
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [editNoteDialogOpen, setEditNoteDialogOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0 = all, 1 = customer, 2 = internal

  const notes = maintenance?.notes || [];
  const customerNotes = notes.filter((n) => n.note_type === 'customer');
  const internalNotes = notes.filter((n) => n.note_type === 'internal');

  const getFilteredNotes = () => {
    switch (activeTab) {
      case 1:
        return customerNotes;
      case 2:
        return internalNotes;
      default:
        return notes;
    }
  };

  const filteredNotes = getFilteredNotes();

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('fi-FI', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Paper elevation={0} sx={{ mt: 3 }}>
      <CardHeader
        title={
          <>
            {t('Notes')}
            {notes.length > 0 && (
              <Chip
                label={notes.length}
                size="small"
                sx={{
                  height: '22px',
                  fontSize: '12px',
                  fontWeight: 600,
                  bgcolor: 'grey.100',
                  ml: 1,
                }}
              />
            )}
          </>
        }
        icon={<ChatBubbleOutlineIcon sx={{ fontSize: 20 }} />}
        action={
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            size="small"
            onClick={() => setNoteDialogOpen(true)}
            sx={{
              borderColor: 'divider',
              color: 'secondary.main',
              borderRadius: '8px',
              fontWeight: 500,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              '&:hover': {
                borderColor: 'secondary.main',
                bgcolor: 'transparent',
              },
            }}
          >
            {t('Add')}
          </Button>
        }
      />

      {/* Tabs for filtering */}
      {notes.length > 0 && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{
              minHeight: '44px',
              '& .MuiTab-root': {
                minHeight: '44px',
                textTransform: 'none',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 500,
                fontSize: '13px',
              },
            }}
          >
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {t('All')}
                  <Chip
                    label={notes.length}
                    size="small"
                    sx={{
                      height: '18px',
                      fontSize: '11px',
                      '& .MuiChip-label': { px: 1 },
                    }}
                  />
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <PublicIcon sx={{ fontSize: 16 }} />
                  {t('Customer')}
                  {customerNotes.length > 0 && (
                    <Chip
                      label={customerNotes.length}
                      size="small"
                      color="primary"
                      sx={{
                        height: '18px',
                        fontSize: '11px',
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                  )}
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <LockIcon sx={{ fontSize: 16 }} />
                  {t('Internal')}
                  {internalNotes.length > 0 && (
                    <Chip
                      label={internalNotes.length}
                      size="small"
                      sx={{
                        height: '18px',
                        fontSize: '11px',
                        bgcolor: 'grey.200',
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                  )}
                </Box>
              }
            />
          </Tabs>
        </Box>
      )}

      <Box sx={{ p: 2.5 }}>
        {filteredNotes.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {filteredNotes.map((note) => (
              <Box
                key={note.id}
                sx={{
                  p: 2,
                  borderRadius: '10px',
                  bgcolor: note.note_type === 'customer' ? 'rgba(49, 130, 206, 0.06)' : 'grey.50',
                  borderLeft: `4px solid ${
                    note.note_type === 'customer' ? '#3182ce' : '#94a3b8'
                  }`,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  '&:hover': {
                    bgcolor: note.note_type === 'customer' ? 'rgba(49, 130, 206, 0.1)' : 'grey.100',
                  },
                }}
                onClick={() => {
                  setSelectedNote(note);
                  setEditNoteDialogOpen(true);
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {note.note_type === 'customer' ? (
                      <>
                        <Chip
                          icon={<PublicIcon sx={{ fontSize: '14px !important' }} />}
                          label={t('Customer visible')}
                          size="small"
                          sx={{
                            height: '24px',
                            bgcolor: 'rgba(49, 130, 206, 0.1)',
                            color: 'secondary.main',
                            fontWeight: 500,
                            fontSize: '11px',
                            '& .MuiChip-icon': {
                              color: 'secondary.main',
                            },
                          }}
                        />
                        {note.show_on_invoice && (
                          <Chip
                            icon={<ReceiptIcon sx={{ fontSize: '14px !important' }} />}
                            label={t('Show on invoice')}
                            size="small"
                            sx={{
                              height: '24px',
                              bgcolor: 'rgba(25, 118, 210, 0.08)',
                              color: 'primary.main',
                              fontWeight: 500,
                              fontSize: '11px',
                              '& .MuiChip-icon': {
                                color: 'primary.main',
                              },
                            }}
                          />
                        )}
                      </>
                    ) : (
                      <Chip
                        icon={<LockIcon sx={{ fontSize: '14px !important' }} />}
                        label={t('Internal only')}
                        size="small"
                        sx={{
                          height: '24px',
                          bgcolor: 'grey.200',
                          color: 'text.secondary',
                          fontWeight: 500,
                          fontSize: '11px',
                          '& .MuiChip-icon': {
                            color: 'text.secondary',
                          },
                        }}
                      />
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedNote(note);
                      setEditNoteDialogOpen(true);
                    }}
                    sx={{
                      opacity: 0.5,
                      '&:hover': { opacity: 1 },
                    }}
                  >
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
                {note.text && (
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: 'text.primary',
                      lineHeight: 1.6,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {note.text}
                  </Typography>
                )}

                {/* Image thumbnails */}
                {note.images && note.images.length > 0 && (
                  <Box
                    sx={{
                      mt: note.text ? 1.5 : 0,
                      display: 'flex',
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    {note.images.map((img) => (
                      <Box
                        key={img.id}
                        component="img"
                        src={img.thumbnail_url || img.url}
                        alt={img.original_filename}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(img.url, '_blank');
                        }}
                        sx={{
                          width: 80,
                          height: 80,
                          objectFit: 'cover',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          border: '1px solid',
                          borderColor: 'divider',
                          transition: 'all 0.15s',
                          '&:hover': {
                            opacity: 0.85,
                            transform: 'scale(1.02)',
                          },
                        }}
                      />
                    ))}
                    {note.images.length > 0 && (
                      <Typography
                        sx={{
                          fontSize: '11px',
                          color: 'text.disabled',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          ml: 0.5,
                        }}
                      >
                        <ImageIcon sx={{ fontSize: 14 }} />
                        {note.images.length}
                      </Typography>
                    )}
                  </Box>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 1.5,
                    pt: 1.5,
                    borderTop: '1px solid',
                    borderColor: note.note_type === 'customer' ? 'rgba(49, 130, 206, 0.1)' : 'grey.200',
                  }}
                >
                  <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                    {note.created_by_name || t('Unknown')}
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>
                    {formatDateTime(note.created_at)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ py: 5, textAlign: 'center', color: 'text.disabled' }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 40, mb: 1.5, opacity: 0.4 }} />
            <Typography sx={{ fontSize: '14px' }}>{t('No notes yet')}</Typography>
            <Typography sx={{ fontSize: '12px', mt: 0.5 }}>
              {t('Add notes to track progress or communicate with customer')}
            </Typography>
          </Box>
        )}
      </Box>

      <AddNoteDialog
        open={noteDialogOpen}
        handleClose={() => setNoteDialogOpen(false)}
        maintenance={maintenance}
        setMaintenance={setMaintenance}
      />
      <EditNoteDialog
        open={editNoteDialogOpen && !!selectedNote}
        handleClose={() => setEditNoteDialogOpen(false)}
        note={selectedNote}
        maintenanceId={maintenance?.id}
        setMaintenance={setMaintenance}
      />
    </Paper>
  );
}

export default NotesSection;
